import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Plus, Trash2, Sparkles, Loader2, ChevronLeft, ChevronRight, ChevronDown,
  CheckCircle2, AlertTriangle, Download, Video, RefreshCcw, Play, Info,
} from "lucide-react";
import clsx from "clsx";
import { api } from "../api";
import { AxisBadge } from "../components/AxisBadge";
import { useModal, Modal } from "../ui/modal";
import { Button, StatusBadge as UiStatusBadge, statusLabel, EmptyState } from "../ui";

const PILLARS = [
  "Barrier", "Hydration", "Soothing", "Exposome",
  "Longevity", "SecondaryUV", "Neuro", "Cross",
];
const LENGTHS = [15, 30, 60, 90] as const;
const RECOMMENDED_CLAIMS: Record<number, number> = { 15: 1, 30: 2, 60: 3, 90: 5 };

interface Scene {
  scene_order: number;
  role: string;
  voice_over: string;
  duration_target_s: number;
  look_id: string;
  references_claim_id: string | null;
  voice_settings?: { speed?: number } | null;
  // Chosen HeyGen voice. undefined/null = "use the avatar's default voice"
  // (byte-identical to pre-picker behaviour). Set via the voice picker.
  voice_id?: string | null;
}

interface Voice {
  voice_id: string;
  name: string;
  language?: string | null;
  gender?: string | null;
  preview_url?: string | null;
  emotion_support?: boolean;
}

// Atlas-Default-Sprechtempo (Pacing-Fix 2026-09-24): Atlas sendet jetzt AKTIV
// 0.90 als Default (langsamer als HeyGens 1.0 / dem alten 0.95, gegen den
// "zu schnell"-Befund — s. DEFAULT_TTS_SPEED in app/routers/heygen.py). Der
// Slider zeigt diesen Wert; nur ein abweichender Wert wird als Override gesendet.
const VOICE_SPEED_DEFAULT = 0.90;

interface HeygenScript {
  id: string;
  source: string;
  article_id: string | null;
  free_topic: string | null;
  pillar_filter: string | null;
  target_length_s: number;
  avatar_id: string;
  language: string;
  narrative_arc: string;
  claim_ids: string[];
  scenes: Scene[];
  compliance_violations: any[] | null;
  status: string;
  video_status: string | null;
  marketing_api_job_id: string | null;
  video_storage_url: string | null;
  video_thumbnail_url: string | null;
  video_duration_s: number | null;
  video_cost_usd: string | null;
  video_error: string | null;
  video_submitted_at: string | null;
  video_completed_at: string | null;
  // Kreditstand vor dem Absenden + Verbrauch (Mig 0100). `null` = nicht
  // gemessen, NICHT „null Credits". Als String, weil NUMERIC so serialisiert.
  wallet_usd_before: string | null;
  wallet_usd_used: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  approved_at: string | null;
  exported_at: string | null;
  // Phase A extension
  content_pillar: string | null;
  predefined_idea_id: string | null;
  concretization: string | null;
  research_background: string | null;
  research_citations: { index: number; url: string; title: string | null }[] | null;
  research_fetched_at: string | null;
  research_model: string | null;
  // Polish-Tab
  polish_settings: PolishSettings | null;
}

interface PolishPresetItem { key: string; label: string }
interface PolishPresets {
  caption_styles: PolishPresetItem[];
  card_presets: PolishPresetItem[];
  music_presets: PolishPresetItem[];
  aspect_ratios: string[];
}

interface PolishSettings {
  captions?: { enabled: boolean; style: string };
  intro?: { enabled: boolean; preset: string; text: string; duration_s: number };
  outro?: { enabled: boolean; preset: string; text: string; duration_s: number };
  background_music?: { enabled: boolean; preset: string; volume: number; audio_fade_s: number };
  video_fade_s?: number;
  // Atlas-intern (kein marketing-api Feld): wenn true, haengt der Server
  // '...' ans Ende der pre-CTA-Szene fuer eine dramatische Pause.
  dramatic_pause_before_cta?: boolean;
}

const POLISH_DEFAULTS: PolishSettings = {
  captions: { enabled: true, style: "bitop_branded" },
  // intro.text wird vom Scene-LLM frisch generiert (3-8 Wort Hook).
  // "Produktschulung" ist nur Placeholder vor Scene-Gen.
  intro: { enabled: true, preset: "bitop_brand", text: "Produktschulung", duration_s: 1.3 },
  outro: { enabled: true, preset: "bitop_brand", text: "The Ectoin® company", duration_s: 1.3 },
  // soft_background = marketing-api's "Ruhig + B2B-konform, AD-Schulungs-default"
  background_music: { enabled: true, preset: "soft_background", volume: 0.2, audio_fade_s: 0.5 },
  video_fade_s: 0.3,
  dramatic_pause_before_cta: true,
};

// Re-polish raw-MP4 retention window per spec §7a
const REPOLISH_WINDOW_DAYS = 60;

// Deep-merge polish_settings ueber POLISH_DEFAULTS: pro feature-Block
// (intro/outro/captions/music) wird der Block-spread aus Defaults gefolgt
// von server-state geschrieben — server-Werte ueberschreiben pro Feld,
// fehlende Felder kriegen Defaults. Damit zeigt UI dieselben Werte die
// dann auch in DB/marketing-api landen.
function mergePolish(server: PolishSettings | null | undefined): PolishSettings {
  const s = server ?? {};
  return {
    captions: { ...POLISH_DEFAULTS.captions!, ...(s.captions ?? {}) },
    intro: { ...POLISH_DEFAULTS.intro!, ...(s.intro ?? {}) },
    outro: { ...POLISH_DEFAULTS.outro!, ...(s.outro ?? {}) },
    background_music: { ...POLISH_DEFAULTS.background_music!, ...(s.background_music ?? {}) },
    video_fade_s: s.video_fade_s ?? POLISH_DEFAULTS.video_fade_s,
    dramatic_pause_before_cta: s.dramatic_pause_before_cta ?? POLISH_DEFAULTS.dramatic_pause_before_cta,
  };
}

interface ContentPillar {
  slug: string;
  display_name: string;
  idea_count: number;
}

interface ContentIdea {
  id: string;
  content_pillar: string;
  idea_number: number;
  title: string;
  suggested_avatar_name: string | null;
  needs_ectoin_emphasis: boolean;
  has_curated_claims: boolean;
}

interface Avatar {
  avatar_id?: string;
  id?: string;
  name?: string;
  preview_url?: string;
  default_look_id?: string;
}

interface Look {
  look_id?: string;
  id?: string;
  name?: string;
  preview_url?: string;
  width?: number | null;
  height?: number | null;
}

type LookFormat = "portrait" | "landscape" | "square";

// Native look format from marketing-api's ffprobe'd width/height (null = unknown).
// The rendered video takes the FIRST scene's look dimensions.
function lookFormat(l?: Look): LookFormat | null {
  if (!l?.width || !l?.height) return null;
  if (l.height > l.width) return "portrait";
  return l.width > l.height ? "landscape" : "square";
}

const FORMAT_LABEL: Record<LookFormat, string> = { portrait: "Portrait", landscape: "Landscape", square: "Square" };

interface ClaimCandidate {
  claim_id: string;
  claim_key: string;
  claim_label: string;
  mechanism_label: string | null;
  pillar: string;
  axis: string | null;
  regulatory_caveats: string | null;
  distance: number;
  abstract_aspect?: string | null;
}

export default function HeyGenStudioPage() {
  const { scriptId } = useParams<{ scriptId?: string }>();
  const navigate = useNavigate();
  const [scripts, setScripts] = useState<HeygenScript[]>([]);
  const [active, setActive] = useState<HeygenScript | null>(null);
  const [creating, setCreating] = useState(false);

  const loadList = useCallback(async () => {
    try {
      const rows = await api.get<HeygenScript[]>("/api/heygen/scripts?limit=100");
      setScripts(rows);
    } catch (e) {
      console.error("list scripts failed", e);
    }
  }, []);

  const loadActive = useCallback(async (id: string) => {
    try {
      const s = await api.get<HeygenScript>(`/api/heygen/scripts/${id}`);
      setActive(s);
    } catch (e) {
      console.error("get script failed", e);
      setActive(null);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    if (scriptId) loadActive(scriptId);
    else setActive(null);
  }, [scriptId, loadActive]);

  // Polling while video is processing
  useEffect(() => {
    if (!active || !active.video_status) return;
    if (!["queued", "generating"].includes(active.video_status)) return;
    const h = setInterval(() => loadActive(active.id), 10_000);
    return () => clearInterval(h);
  }, [active?.id, active?.video_status, loadActive]);

  // Mobile: genau eine Pane (§0.7) — Liste ODER Detail. detailShown steuert,
  // welche Spalte auf < md sichtbar ist; Desktop zeigt beide.
  const detailShown = creating || !!scriptId;

  return (
    <div className="flex h-screen overflow-hidden">
      <aside
        className={clsx(
          "shrink-0 border-r border-cream-200 dark:border-ink-600 bg-cream-100 dark:bg-ink-800 flex-col w-full md:w-72",
          detailShown ? "hidden md:flex" : "flex"
        )}
      >
        <div className="p-3 border-b border-cream-200 dark:border-ink-600 flex items-center justify-between">
          <h2 className="font-display font-semibold text-sm">Video-Skripte</h2>
          <button
            onClick={() => setCreating(true)}
            className="p-1.5 rounded-md bg-brand-500 text-white hover:bg-brand-600"
            title="Neues Video-Skript"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {scripts.map((s) => (
            <button
              key={s.id}
              onClick={() => navigate(`/heygen/${s.id}`)}
              className={clsx(
                "w-full text-left px-2.5 py-2 rounded-md text-sm transition-colors",
                active?.id === s.id
                  ? "bg-brand-50 dark:bg-ink-700 border-l-2 border-brand-500"
                  : "hover:bg-cream-200/60 dark:hover:bg-ink-700/60"
              )}
            >
              <div className="font-medium truncate">{s.free_topic ?? "(ohne Titel)"}</div>
              <div className="flex items-center gap-2 mt-1">
                <StatusBadge status={s.status} />
                {s.video_status && <VideoStatusBadge status={s.video_status} />}
                <span className="text-[10px] text-text-3">≈ {s.target_length_s} s Video</span>
              </div>
            </button>
          ))}
          {scripts.length === 0 && (
            <EmptyState
              className="py-8 px-4"
              icon={<Video className="w-6 h-6" />}
              title="Noch keine Skripte"
              hint="Starte dein erstes Video-Skript aus einem Content-Pillar oder frei."
              action={
                <Button variant="secondary" onClick={() => setCreating(true)}>
                  Neues Skript
                </Button>
              }
            />
          )}
        </div>
      </aside>

      <main
        className={clsx(
          "flex-1 overflow-auto",
          detailShown ? "block" : "hidden md:block"
        )}
      >
        {detailShown && (
          <div className="md:hidden sticky top-0 z-10 bg-cream-100 dark:bg-ink-800 border-b border-cream-200 dark:border-ink-600 px-3 py-2">
            <button
              onClick={() => {
                setCreating(false);
                navigate("/heygen");
              }}
              className="inline-flex items-center gap-1 text-sm text-ink-50/70 hover:text-brand-600"
            >
              <ChevronLeft className="w-4 h-4" /> Zurück zur Liste
            </button>
          </div>
        )}
        {creating ? (
          <CreateScriptWizard
            onCancel={() => setCreating(false)}
            onCreated={async (s) => {
              setCreating(false);
              await loadList();
              navigate(`/heygen/${s.id}`);
            }}
          />
        ) : active ? (
          <ScriptDetail
            script={active}
            onChange={(s) => setActive(s)}
            onDelete={async () => {
              await api.delete(`/api/heygen/scripts/${active.id}`);
              await loadList();
              navigate("/heygen");
            }}
            onReload={() => loadActive(active.id)}
          />
        ) : (
          <div className="h-full grid place-items-center">
            <EmptyState
              icon={<Video className="w-6 h-6" />}
              title="Kein Skript ausgewählt"
              hint="Wähle links ein Video-Skript aus oder lege ein neues an."
              action={
                <Button variant="secondary" onClick={() => setCreating(true)}>
                  Neues Skript
                </Button>
              }
            />
          </div>
        )}
      </main>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const info = statusLabel(status);
  return <UiStatusBadge status={info.status}>{info.label}</UiStatusBadge>;
}

function VideoStatusBadge({ status }: { status: string }) {
  const info = statusLabel(status);
  return <UiStatusBadge status={info.status}>Video: {info.label}</UiStatusBadge>;
}

// ----------------------------------------------------------------
// Create wizard (§13.2)
// ----------------------------------------------------------------

function CreateScriptWizard({ onCancel, onCreated }: {
  onCancel: () => void;
  onCreated: (s: HeygenScript) => void;
}) {
  // Mode: "pillar" (Content-Pillar+Idea workflow) or "custom" (free-topic only, legacy)
  const [mode, setMode] = useState<"pillar" | "custom">("pillar");
  const [pillars, setPillars] = useState<ContentPillar[]>([]);
  const [pillarSlug, setPillarSlug] = useState<string>("");
  const [ideas, setIdeas] = useState<ContentIdea[]>([]);
  const [ideaId, setIdeaId] = useState<string>("");
  const [freeTopic, setFreeTopic] = useState("");
  const [cosmeticPillarFilter, setCosmeticPillarFilter] = useState<string>("");
  const [length, setLength] = useState<15 | 30 | 60 | 90>(60);
  const [avatars, setAvatars] = useState<Avatar[]>([]);
  const [avatarId, setAvatarId] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Avatars + Content-Pillars laden on mount
  useEffect(() => {
    api.get<Avatar[]>("/api/heygen/avatars")
      .then((rows) => {
        setAvatars(rows);
        if (rows[0] && !avatarId) setAvatarId(rows[0].avatar_id ?? rows[0].id ?? "");
      })
      .catch((e) => setError(`Could not load avatars: ${e.message}`));
    api.get<ContentPillar[]>("/api/heygen/content-pillars")
      .then(setPillars)
      .catch((e) => console.warn("pillars load failed", e));
  }, []);

  // Ideas laden wenn Pillar gewechselt
  useEffect(() => {
    if (mode !== "pillar" || !pillarSlug) {
      setIdeas([]); setIdeaId("");
      return;
    }
    api.get<ContentIdea[]>(`/api/heygen/content-pillars/${pillarSlug}/ideas`)
      .then((rows) => {
        setIdeas(rows);
        setIdeaId("");
      })
      .catch((e) => setError(`Could not load ideas: ${e.message}`));
  }, [mode, pillarSlug]);

  // Wenn Idea gewählt: free_topic + Avatar auto-fillen
  useEffect(() => {
    if (!ideaId) return;
    const idea = ideas.find((i) => i.id === ideaId);
    if (!idea) return;
    setFreeTopic(idea.title.slice(0, 200));
    if (idea.suggested_avatar_name && avatars.length > 0) {
      const needle = idea.suggested_avatar_name.toLowerCase().trim();
      const match = avatars.find((a) => (a.name ?? "").toLowerCase().trim().startsWith(needle));
      if (match) setAvatarId(match.avatar_id ?? match.id ?? "");
    }
  }, [ideaId, ideas, avatars]);

  const selectedIdea = ideas.find((i) => i.id === ideaId) || null;

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const body: any = {
        free_topic: freeTopic,
        pillar_filter: cosmeticPillarFilter || null,
        target_length_s: length,
        avatar_id: avatarId,
        language: "en",
      };
      if (mode === "pillar" && pillarSlug) {
        body.content_pillar = pillarSlug;
        if (ideaId) body.predefined_idea_id = ideaId;
      }
      const created = await api.post<HeygenScript>("/api/heygen/scripts", body);
      onCreated(created);
    } catch (e: any) {
      setError(e?.message || "create failed");
    } finally {
      setBusy(false);
    }
  };

  const canSubmit = freeTopic.trim().length > 0 && avatarId && !busy
    && (mode === "custom" || (pillarSlug && ideaId));

  return (
    <div className="max-w-2xl mx-auto p-8">
      <h1 className="text-2xl font-display font-semibold mb-2">Neues Video-Skript</h1>

      {/* Mode toggle */}
      <div className="flex gap-1 mb-6 p-1 bg-cream-100 dark:bg-ink-700 rounded-md w-fit">
        {([
          { id: "pillar", label: "From content pillar + idea" },
          { id: "custom", label: "Custom topic" },
        ] as const).map((m) => (
          <button
            key={m.id}
            onClick={() => setMode(m.id)}
            className={clsx(
              "px-3 py-1.5 rounded-md text-xs",
              mode === m.id ? "bg-surface dark:bg-ink-900 shadow-sm" : "hover:bg-cream-200/60 dark:hover:bg-ink-600"
            )}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {mode === "pillar" && (
          <>
            <div>
              <label className="block text-sm font-medium mb-1">Content Pillar <span className="text-danger">*</span></label>
              <select
                value={pillarSlug}
                onChange={(e) => setPillarSlug(e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-cream-200 dark:border-ink-600 bg-cream-50 dark:bg-ink-800 text-sm"
              >
                <option value="">— Pick a pillar —</option>
                {pillars.map((p) => (
                  <option key={p.slug} value={p.slug}>{p.display_name} ({p.idea_count} ideas)</option>
                ))}
              </select>
            </div>
            {pillarSlug && (
              <div>
                <label className="block text-sm font-medium mb-1">Idea <span className="text-danger">*</span></label>
                <select
                  value={ideaId}
                  onChange={(e) => setIdeaId(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-cream-200 dark:border-ink-600 bg-cream-50 dark:bg-ink-800 text-sm"
                >
                  <option value="">— Pick an idea —</option>
                  {ideas.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.idea_number}. {i.title}
                      {i.needs_ectoin_emphasis ? " ★" : ""}
                      {i.has_curated_claims ? " (curated)" : ""}
                    </option>
                  ))}
                </select>
                {selectedIdea?.needs_ectoin_emphasis && (
                  <div className="text-xs text-warning mt-1">
                    ★ Marked in xlsx as needing strong Ectoin emphasis
                  </div>
                )}
                {selectedIdea?.suggested_avatar_name && (
                  <div className="text-xs text-ink-50/60 mt-1">
                    Suggested avatar: <span className="font-medium">{selectedIdea.suggested_avatar_name}</span> (auto-filled below, overridable)
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <div>
          <label className="block text-sm font-medium mb-1">
            {mode === "pillar" ? "Topic / angle" : "Topic / research notes"} <span className="text-danger">*</span>
          </label>
          <textarea
            value={freeTopic}
            onChange={(e) => setFreeTopic(e.target.value.slice(0, 2500))}
            rows={mode === "custom" ? 6 : 2}
            placeholder={mode === "pillar"
              ? "Auto-filled from idea — edit to narrow the angle (e.g. 'Inflammaging as the new Anti-Aging')"
              : "Paste topic + supporting research / Mintel stats / study findings. The scene-writer uses numbers from here for hook/problem framing (market context only — NOT as Ectoin efficacy)."}
            className="w-full px-3 py-2 rounded-md border border-cream-200 dark:border-ink-600 bg-cream-50 dark:bg-ink-800 text-sm font-mono"
          />
          <div className="text-xs text-ink-50/60 mt-1">
            {freeTopic.length}/2500 · feeds claim search, Perplexity research, and hook/problem scene framing
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {mode === "custom" && (
            <div>
              <label className="block text-sm font-medium mb-1">Cosmetic-Pillar filter</label>
              <select
                value={cosmeticPillarFilter}
                onChange={(e) => setCosmeticPillarFilter(e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-cream-200 dark:border-ink-600 bg-cream-50 dark:bg-ink-800 text-sm"
              >
                <option value="">Any</option>
                {PILLARS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">Length</label>
            <select
              value={length}
              onChange={(e) => setLength(Number(e.target.value) as 15 | 30 | 60 | 90)}
              className="w-full px-3 py-2 rounded-md border border-cream-200 dark:border-ink-600 bg-cream-50 dark:bg-ink-800 text-sm"
            >
              {LENGTHS.map((l) => <option key={l} value={l}>{l} sec ({RECOMMENDED_CLAIMS[l]} claim{RECOMMENDED_CLAIMS[l] > 1 ? "s" : ""})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Avatar</label>
            <select
              value={avatarId}
              onChange={(e) => setAvatarId(e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-cream-200 dark:border-ink-600 bg-cream-50 dark:bg-ink-800 text-sm"
            >
              {avatars.map((a) => {
                const id = a.avatar_id ?? a.id ?? "";
                return <option key={id} value={id}>{a.name ?? id}</option>;
              })}
            </select>
          </div>
        </div>

        {error && <div className="text-sm text-danger">{error}</div>}

        <div className="flex gap-2 pt-2">
          <Button onClick={submit} disabled={!canSubmit} loading={busy} size="lg">
            Erstellen + zum Claim-Picker
          </Button>
          <button onClick={onCancel} className="px-4 py-2 text-sm">Abbrechen</button>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------
// Script detail (claim picker / scene editor / approve / video)
// ----------------------------------------------------------------

function ScriptDetail({ script, onChange, onDelete, onReload }: {
  script: HeygenScript;
  onChange: (s: HeygenScript) => void;
  onDelete: () => void;
  onReload: () => void;
}) {
  const modal = useModal();
  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <header className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-display font-semibold truncate">{script.free_topic}</h1>
          <div className="flex items-center gap-2 mt-1 text-xs text-ink-50/60">
            <StatusBadge status={script.status} />
            {script.video_status && <VideoStatusBadge status={script.video_status} />}
            <span>{script.target_length_s}s · {script.narrative_arc}</span>
            <span>· avatar: {script.avatar_id}</span>
          </div>
        </div>
        <div className="flex gap-1 shrink-0">
          <button onClick={onReload} className="p-2 rounded-md hover:bg-cream-200/60 dark:hover:bg-ink-700" title="Reload">
            <RefreshCcw className="w-4 h-4" />
          </button>
          <button
            onClick={async () => {
              const ok = await modal.confirm({
                title: "Delete this script?",
                confirmLabel: "Delete",
                destructive: true,
              });
              if (ok) onDelete();
            }}
            className="p-2 rounded-md hover:bg-danger-soft text-danger"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Claim picker — claim_picking, scene_building */}
      {["claim_picking", "scene_building"].includes(script.status) && (
        <ClaimPickerSection script={script} onChange={onChange} />
      )}

      {/* Scene editor — scene_building (after picks), generated, compliance_blocked */}
      {["scene_building", "generated", "compliance_blocked", "approved", "exported"].includes(script.status)
        && script.scenes.length > 0 && (
        <SceneEditorSection script={script} onChange={onChange} />
      )}

      {/* Research-Sources Panel (collapsed by default, only if research run) */}
      {script.scenes.length > 0 && script.research_background && (
        <ResearchSourcesPanel script={script} />
      )}

      {/* Compliance violations */}
      {script.compliance_violations && script.compliance_violations.length > 0 && (
        <ComplianceViolations script={script} onChange={onChange} />
      )}

      {/* Polish-Tab (optional) — captions/intro/outro/music. Mounts before
          Approve so user can set picks before video generation. */}
      {["generated", "compliance_blocked", "approved", "exported"].includes(script.status) && (
        <PolishTab script={script} onChange={onChange} />
      )}

      {/* Approve + Generate Video — also mounts on compliance_blocked so
          user can override + retry from the same panel. */}
      {["generated", "compliance_blocked", "approved", "exported"].includes(script.status) && (
        <ApproveAndVideoSection script={script} onChange={onChange} />
      )}
    </div>
  );
}

type CandidateClaim = Omit<ClaimCandidate, "distance"> & {
  source?: "curated" | "auto";
  distance: number | null;
};

type CandidatesResp =
  | { candidates: CandidateClaim[]; recommended_count_for_length: number }
  | { curated: CandidateClaim[]; other: CandidateClaim[]; recommended_count_for_length: number };

function _isSectioned(r: CandidatesResp): r is { curated: CandidateClaim[]; other: CandidateClaim[]; recommended_count_for_length: number } {
  return Array.isArray((r as any).curated);
}

function ClaimPickerSection({ script, onChange }: {
  script: HeygenScript;
  onChange: (s: HeygenScript) => void;
}) {
  const [curated, setCurated] = useState<CandidateClaim[]>([]);
  const [other, setOther] = useState<CandidateClaim[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [recommended, setRecommended] = useState(RECOMMENDED_CLAIMS[script.target_length_s] ?? 2);
  const [selected, setSelected] = useState<Set<string>>(new Set(script.claim_ids));
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [busyMode, setBusyMode] = useState<"" | "save" | "scenes" | "research">("");
  const [error, setError] = useState<string | null>(null);

  const usesIdea = !!script.predefined_idea_id;

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.post<CandidatesResp>(
        `/api/heygen/scripts/${script.id}/candidate-claims`
      );
      if (_isSectioned(r)) {
        setCurated(r.curated); setOther(r.other);
      } else {
        setCurated([]); setOther(r.candidates);
      }
      setRecommended(r.recommended_count_for_length);
      setLoaded(true);
    } catch (e: any) {
      setError(e?.message || "load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (script.claim_ids.length === 0 && !loaded) load();
  }, [script.id]);

  const toggle = (id: string) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const persistPicks = async (): Promise<void> => {
    const selectedArr = Array.from(selected);
    const sameAsServer =
      script.claim_ids.length === selectedArr.length &&
      selectedArr.every((id) => script.claim_ids.includes(id)) &&
      script.status !== "claim_picking";
    if (!sameAsServer) {
      await api.post<HeygenScript>(
        `/api/heygen/scripts/${script.id}/pick-claims`,
        { claim_ids: selectedArr }
      );
    }
  };

  const submit = async () => {
    setBusy(true); setBusyMode("save"); setError(null);
    try {
      await persistPicks();
      // Re-fetch to update parent state
      const fresh = await api.get<HeygenScript>(`/api/heygen/scripts/${script.id}`);
      onChange(fresh);
    } catch (e: any) {
      setError(e?.message || "save failed");
    } finally {
      setBusy(false); setBusyMode("");
    }
  };

  const generate = async () => {
    setBusy(true); setBusyMode("scenes"); setError(null);
    try {
      await persistPicks();
      const updated = await api.post<HeygenScript>(
        `/api/heygen/scripts/${script.id}/generate-scenes`,
        {}
      );
      onChange(updated);
    } catch (e: any) {
      setError(e?.message || "generate failed");
    } finally {
      setBusy(false); setBusyMode("");
    }
  };

  const researchAndGenerate = async () => {
    setBusy(true); setBusyMode("research"); setError(null);
    try {
      await persistPicks();
      const updated = await api.post<HeygenScript>(
        `/api/heygen/scripts/${script.id}/research-and-generate`
      );
      onChange(updated);
    } catch (e: any) {
      setError(e?.message || "research+generate failed");
    } finally {
      setBusy(false); setBusyMode("");
    }
  };

  const renderClaim = (c: CandidateClaim) => {
    const isSel = selected.has(c.claim_id);
    const d = c.distance;
    // Re-labeled distance: "close/related/broad" — niedrige Distanz heißt
    // semantisch nah, hohe kann durchaus thematisch passend sein (abstraktes
    // Topic vs konkrete Claim-Sprache).
    const distLabel = d === null
      ? null
      : d < 0.30 ? { tone: "close" as const, color: "bg-success-soft text-success",
                     tip: "very close semantic match to your topic" }
      : d < 0.50 ? { tone: "related" as const, color: "bg-warning-soft text-warning",
                     tip: "related to your topic" }
      : { tone: "broad" as const, color: "bg-cream-200 text-ink-50 dark:bg-ink-700",
          tip: "broader match — common when the topic is abstract (e.g. 'inflammaging'). Read the claim text to judge fit." };
    return (
      <button
        key={c.claim_id}
        onClick={() => toggle(c.claim_id)}
        className={clsx(
          "w-full text-left p-2 rounded-md border transition-colors",
          isSel
            ? "border-brand-500 bg-brand-50/50 dark:bg-brand-900/20"
            : "border-cream-200 dark:border-ink-600 hover:bg-cream-100 dark:hover:bg-ink-700"
        )}
      >
        <div className="flex items-start gap-2">
          <input type="checkbox" checked={isSel} readOnly className="mt-1" />
          <div className="flex-1 min-w-0">
            {c.abstract_aspect && (
              <div className="text-xs font-semibold text-brand-700 dark:text-brand-300 mb-0.5 flex items-center gap-1.5">
                <span className="inline-block w-1 h-1 rounded-full bg-brand-500" />
                {c.abstract_aspect}
              </div>
            )}
            <div className="text-sm text-ink">{c.claim_label}</div>
            {c.mechanism_label && (
              <div className="text-xs text-ink-50/60 mt-0.5">{c.mechanism_label}</div>
            )}
            <div className="flex items-center gap-2 mt-1 text-[10px]">
              <span className="text-ink-50/50">{c.pillar}</span>
              <AxisBadge axis={c.axis} />
              {distLabel && (
                <span className={clsx("px-1.5 py-0.5 rounded-md font-medium", distLabel.color)} title={distLabel.tip}>
                  {distLabel.tone} ({(d as number).toFixed(2)})
                </span>
              )}
            </div>
          </div>
        </div>
      </button>
    );
  };

  const hasAnyCandidates = curated.length > 0 || other.length > 0;
  // "All broad" warning only fires if EVERY hit is broad — for abstract topics
  // (Inflammaging etc.) broad hits can still be thematically right, so the
  // tone is suggesting refinement rather than alarming.
  const allBroad = loaded && hasAnyCandidates
    && curated.length === 0
    && other.every((c) => (c.distance ?? 0) >= 0.55);

  return (
    <section className="bg-cream-50 dark:bg-ink-800 border border-cream-200 dark:border-ink-600 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-medium">Which aspects do you want to emphasize?</h2>
        <button onClick={load} disabled={loading} className="text-xs text-brand-600 hover:underline flex items-center gap-1">
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCcw className="w-3 h-3" />}
          Re-search
        </button>
      </div>
      <div className="text-xs text-ink-50/60 mb-3">
        {usesIdea ? (
          <>Claims for this idea (pillar→axis filtered + semantic-ranked
            {script.concretization ? <> by topic + concretization</> : <> by idea title</>}).</>
        ) : (
          <>Semantic search of <em>"{script.free_topic}"</em> against approved claims.</>
        )}
        {" "}Selected: <span className={clsx(
          "font-medium",
          selected.size > recommended ? "text-warning" : "text-brand-600"
        )}>{selected.size}</span> / recommended for {script.target_length_s}s: {recommended}.
        {selected.size > recommended && <span className="text-warning"> (above recommended — may exceed length budget)</span>}
      </div>
      {error && <div className="text-sm text-danger mb-2">{error}</div>}
      <div className="space-y-3 max-h-[500px] overflow-y-auto">
        {curated.length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-50/50 mb-1.5 px-1">
              ★ Curated for this idea ({curated.length})
            </div>
            <div className="space-y-2">{curated.map(renderClaim)}</div>
          </div>
        )}
        {other.length > 0 && (
          <div>
            {curated.length > 0 && (
              <div className="text-xs uppercase tracking-wider text-ink-50/50 mb-1.5 px-1">
                Other matching ({other.length})
              </div>
            )}
            <div className="space-y-2">{other.map(renderClaim)}</div>
          </div>
        )}
        {loaded && !hasAnyCandidates && (
          <div className="text-sm text-ink-50/60 text-center py-4">No approved claims match this topic.</div>
        )}
        {allBroad && (
          <div className="text-xs text-ink-50/70 bg-cream-100 dark:bg-ink-900/40 border border-cream-200 dark:border-ink-700 rounded-md p-2">
            ℹ Distances are all broad (≥ 0.55). For abstract topics like "Inflammaging" or
            "Anti-Aging" this is expected — the claims address the concept indirectly via
            their mechanism. <strong>Read the aspect tags + claim text</strong> to judge fit.
          </div>
        )}
      </div>
      {busyMode === "research" && (
        <div className="mt-3 text-sm text-accent-700 bg-accent-50 border border-accent-200 rounded-md px-3 py-2 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          Researching with Perplexity (5-15s), then generating scenes…
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-4">
        <Button
          variant="secondary"
          onClick={submit}
          disabled={selected.size === 0 || busy}
          loading={busyMode === "save"}
        >
          Nur Auswahl speichern
        </Button>
        {usesIdea ? (
          <Button
            onClick={researchAndGenerate}
            disabled={selected.size === 0 || busy}
            loading={busyMode === "research"}
            icon={<Sparkles className="w-3 h-3" />}
          >
            {script.scenes.length > 0 ? "Neu recherchieren + neu erzeugen" : "Recherchieren + Szenen erzeugen"}
          </Button>
        ) : (
          <Button
            onClick={generate}
            disabled={selected.size === 0 || busy}
            loading={busyMode === "scenes"}
            icon={<Sparkles className="w-3 h-3" />}
          >
            {script.scenes.length > 0 ? "Szenen neu erzeugen" : "Speichern + Szenen erzeugen"}
          </Button>
        )}
      </div>
    </section>
  );
}

function SceneEditorSection({ script, onChange }: {
  script: HeygenScript;
  onChange: (s: HeygenScript) => void;
}) {
  const modal = useModal();
  const [busyScene, setBusyScene] = useState<number | null>(null);
  const [busyAll, setBusyAll] = useState(false);
  const [looksByAvatar, setLooksByAvatar] = useState<Record<string, Look[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [scenes, setScenes] = useState<Scene[]>(script.scenes);

  useEffect(() => { setScenes(script.scenes); }, [script.id, script.scenes]);

  useEffect(() => {
    if (looksByAvatar[script.avatar_id]) return;
    api.get<Look[]>(`/api/heygen/avatars/${encodeURIComponent(script.avatar_id)}/looks`)
      .then((rows) => setLooksByAvatar((m) => ({ ...m, [script.avatar_id]: rows })))
      .catch((e) => console.warn("looks load failed", e));
  }, [script.avatar_id]);

  const looks = looksByAvatar[script.avatar_id] ?? [];
  const [formatFilter, setFormatFilter] = useState<LookFormat | "all">("all");
  const cycleLooks = formatFilter === "all" ? looks : looks.filter((l) => lookFormat(l) === formatFilter);
  const editable = !["approved", "exported"].includes(script.status);

  // Dirty-check: lokales State (scenes) != server-Stand (script.scenes).
  // Wird in Save-buttons/Approve gewarnt.
  const dirty = JSON.stringify(scenes) !== JSON.stringify(script.scenes);

  const persistScenes = async (next: Scene[]): Promise<void> => {
    const updated = await api.patch<HeygenScript>(`/api/heygen/scripts/${script.id}`, {
      scenes: next.map((s) => ({ ...s })),
    });
    onChange(updated);
  };

  const cycleLook = async (idx: number, dir: 1 | -1) => {
    if (!editable || cycleLooks.length === 0) return;
    const cur = scenes[idx].look_id;
    const i = cycleLooks.findIndex((l) => (l.look_id ?? l.id) === cur);
    const next = i < 0 ? cycleLooks[0] : cycleLooks[(i + dir + cycleLooks.length) % cycleLooks.length];
    const nextId = next.look_id ?? next.id ?? cur;
    const newScenes = scenes.map((x, k) => k === idx ? { ...x, look_id: nextId } : x);
    // Optimistic UI update + auto-persist (look changes sind selten, jeder
    // Click ist klare User-Intent — kein manueller Save danach noetig).
    setScenes(newScenes);
    setError(null);
    try {
      await persistScenes(newScenes);
    } catch (e: any) {
      setError(`Look save failed: ${e?.message || "?"} — click "Save scenes" to retry`);
      // Lokales State bleibt; "Save scenes"-Button retries.
    }
  };

  const updateVoiceOver = (idx: number, vo: string) => {
    if (!editable) return;
    setScenes((s) => s.map((x, k) => k === idx ? { ...x, voice_over: vo.slice(0, 250) } : x));
  };

  const blurVoiceOver = async (idx: number) => {
    // Auto-save on textarea blur, falls voice_over geaendert wurde
    if (!editable) return;
    const cur = scenes[idx];
    const serverScene = script.scenes[idx];
    if (!serverScene || cur.voice_over === serverScene.voice_over) return;
    setError(null);
    try {
      await persistScenes(scenes);
    } catch (e: any) {
      setError(`voice-over save failed: ${e?.message || "?"} — click "Save scenes" to retry`);
    }
  };

  const updateVoiceSpeed = (idx: number, speed: number) => {
    if (!editable) return;
    setScenes(s => s.map((x, k) => k === idx
      ? { ...x, voice_settings: { ...(x.voice_settings ?? {}), speed } }
      : x,
    ));
  };

  const commitVoiceSpeed = async (idx: number) => {
    // Auto-save on slider release. Vergleich gegen server-state damit wir
    // nicht bei jedem onMouseUp PATCHen wenn nichts geaendert.
    if (!editable) return;
    const cur = scenes[idx]?.voice_settings?.speed;
    const srv = script.scenes[idx]?.voice_settings?.speed;
    if (cur === srv) return;
    setError(null);
    try {
      await persistScenes(scenes);
    } catch (e: any) {
      setError(`voice-speed save failed: ${e?.message || "?"} — click "Save scenes" to retry`);
    }
  };

  const saveScenes = async () => {
    setBusyAll(true); setError(null);
    try {
      const updated = await api.patch<HeygenScript>(`/api/heygen/scripts/${script.id}`, {
        scenes: scenes.map((s) => ({ ...s })),
      });
      onChange(updated);
    } catch (e: any) {
      setError(e?.message || "save failed");
    } finally {
      setBusyAll(false);
    }
  };

  // Voice is chosen per-script (applied to every scene). null = "avatar's
  // default voice" → scene.voice_id omitted → byte-identical to pre-picker.
  const applyVoiceToAll = async (voiceId: string | null) => {
    if (!editable) return;
    const next = scenes.map((s) => ({ ...s, voice_id: voiceId ?? undefined }));
    setScenes(next);
    setError(null);
    try {
      await persistScenes(next);
    } catch (e: any) {
      setError(`voice save failed: ${e?.message || "?"} — click "Force save" to retry`);
    }
  };

  const deleteScene = async (idx: number) => {
    if (!editable) return;
    if (scenes.length <= 1) {
      setError("Cannot delete the only remaining scene.");
      return;
    }
    const ok = await modal.confirm({
      title: `Delete scene ${idx + 1} (${scenes[idx].role})?`,
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    setBusyScene(idx); setError(null);
    try {
      // Flush any unsaved local changes first so backend has the same state
      if (dirty) await persistScenes(scenes);
      const updated = await api.delete<HeygenScript>(
        `/api/heygen/scripts/${script.id}/scenes/${idx}`
      );
      onChange(updated);
    } catch (e: any) {
      setError(e?.message || "delete failed");
    } finally {
      setBusyScene(null);
    }
  };

  const regenerateScene = async (idx: number, hint?: string) => {
    setBusyScene(idx); setError(null);
    try {
      // Save scenes first
      await api.patch<HeygenScript>(`/api/heygen/scripts/${script.id}`, {
        scenes: scenes.map((s) => ({ ...s })),
      });
      const updated = await api.post<HeygenScript>(
        `/api/heygen/scripts/${script.id}/scenes/${idx}/regenerate`,
        hint ? { hint } : undefined,
      );
      onChange(updated);
    } catch (e: any) {
      setError(e?.message || "regenerate failed");
    } finally {
      setBusyScene(null);
    }
  };

  const totalDuration = scenes.reduce((sum, s) => sum + (s.duration_target_s || 0), 0);
  const formatsInLooks = (["portrait", "landscape", "square"] as LookFormat[]).filter((f) => looks.some((l) => lookFormat(l) === f));
  const sceneFormats = new Set(
    scenes.map((s) => lookFormat(looks.find((l) => (l.look_id ?? l.id) === s.look_id))).filter(Boolean),
  );

  return (
    <section className="bg-cream-50 dark:bg-ink-800 border border-cream-200 dark:border-ink-600 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-medium flex items-center gap-2">
          Scenes ({scenes.length}, ~{totalDuration}s)
          {dirty && (
            <span className="text-[10px] uppercase font-medium text-warning bg-warning-soft px-1.5 py-0.5 rounded-md flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" />
              saving
            </span>
          )}
        </h2>
        {editable && (
          <button
            onClick={saveScenes}
            disabled={busyAll || !dirty}
            className="text-xs text-brand-600 hover:underline disabled:opacity-30 disabled:no-underline flex items-center gap-1"
            title="Manually flush any unsaved scene changes (auto-save runs on look cycle + voice-over blur)"
          >
            {busyAll && <Loader2 className="w-3 h-3 animate-spin" />}
            Force save
          </button>
        )}
      </div>
      {formatsInLooks.length > 1 && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-3">
          <span className="text-xs font-medium text-ink-50/70">Look format</span>
          {(["all", ...formatsInLooks] as (LookFormat | "all")[]).map((f) => (
            <button
              key={f}
              onClick={() => setFormatFilter(f)}
              className={`text-xs px-2 py-0.5 rounded-md border ${formatFilter === f
                ? "border-brand-600 bg-brand-100 text-brand-800 dark:bg-brand-900/40 dark:text-brand-300"
                : "border-cream-200 dark:border-ink-600 hover:bg-cream-200 dark:hover:bg-ink-700"}`}
            >
              {f === "all" ? "All" : FORMAT_LABEL[f]} ({f === "all" ? looks.length : looks.filter((l) => lookFormat(l) === f).length})
            </button>
          ))}
          <span className="text-[10px] text-ink-50/50">arrows cycle only through this format</span>
          {sceneFormats.size > 1 && (
            <span className="text-[10px] text-warning bg-warning-soft px-1.5 py-0.5 rounded-md">
              scenes mix formats — the video uses scene 1&apos;s format, other scenes get letterboxed
            </span>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-3 pb-3 border-b border-cream-200 dark:border-ink-700">
        <span className="text-xs font-medium text-ink-50/70">Voice</span>
        <VoicePicker
          value={scenes[0]?.voice_id ?? null}
          disabled={!editable}
          onChange={applyVoiceToAll}
        />
        <span className="text-[10px] text-ink-50/50">
          applies to every scene · default keeps the avatar&apos;s own voice
        </span>
      </div>
      {error && <div className="text-sm text-danger mb-2">{error}</div>}
      <div className="space-y-3">
        {scenes.map((s, idx) => {
          const curLook = looks.find((l) => (l.look_id ?? l.id) === s.look_id);
          const curFormat = lookFormat(curLook);
          return (
            <div key={idx} className="border border-cream-200 dark:border-ink-600 rounded-md p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-ink-50/60">Scene {s.scene_order}</span>
                <span className="px-1.5 py-0.5 rounded-md text-[10px] bg-brand-100 text-brand-800 dark:bg-brand-900/40 dark:text-brand-300 uppercase font-medium">
                  {s.role}
                </span>
                <span className="text-xs text-ink-50/60 ml-auto">~{s.duration_target_s}s</span>
                {editable && (
                  <button
                    onClick={() => deleteScene(idx)}
                    disabled={busyScene === idx || scenes.length <= 1}
                    className="p-1 rounded-md hover:bg-danger-soft text-danger disabled:opacity-30 disabled:cursor-not-allowed"
                    title={scenes.length <= 1 ? "cannot delete the only scene" : "delete this scene"}
                  >
                    {busyScene === idx ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-[160px_1fr] gap-3">
                <div className="space-y-1">
                  <div className="relative bg-cream-200 dark:bg-ink-700 rounded-md aspect-square grid place-items-center text-xs text-ink-50/60 overflow-hidden">
                    {curFormat && (
                      <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-ink-900/70 text-cream-50">
                        {FORMAT_LABEL[curFormat]}
                      </span>
                    )}
                    {curLook?.preview_url ? (
                      <img src={curLook.preview_url} alt={curLook.name ?? ""} className="w-full h-full object-cover" />
                    ) : (
                      <span>{s.look_id?.slice(0, 16) || "(no look)"}</span>
                    )}
                  </div>
                  {editable && (
                    <div className="flex justify-between items-center">
                      <button onClick={() => cycleLook(idx, -1)} className="p-1 rounded-md hover:bg-cream-200">
                        <ChevronLeft className="w-3 h-3" />
                      </button>
                      <span className="text-[10px] text-ink-50/60 truncate">{curLook?.name ?? s.look_id}</span>
                      <button onClick={() => cycleLook(idx, 1)} className="p-1 rounded-md hover:bg-cream-200">
                        <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <div className="relative">
                    <textarea
                      value={s.voice_over}
                      disabled={!editable}
                      onChange={(e) => updateVoiceOver(idx, e.target.value)}
                      onBlur={() => blurVoiceOver(idx)}
                      rows={3}
                      className="w-full pl-2 pr-7 py-1.5 rounded-md border border-cream-200 dark:border-ink-600 bg-surface dark:bg-ink-900 text-sm font-serif italic resize-none"
                    />
                    <span
                      className="absolute top-1.5 right-1 text-ink-50/40 hover:text-ink-50/80 cursor-help group"
                      tabIndex={0}
                    >
                      <Info className="w-3.5 h-3.5" />
                      <span className="invisible group-hover:visible group-focus:visible absolute right-0 top-5 z-10 w-72 p-3 rounded-md border border-cream-300 dark:border-ink-600 bg-cream-50 dark:bg-ink-800 shadow-lg text-xs text-ink-50/80 font-sans not-italic normal-case leading-relaxed">
                        <strong>Punctuation = Sprach-Dynamik.</strong> Unsere ectocare-Voices haben keine emotion-support, also entstehen Pausen durch Satzzeichen:
                        <ul className="mt-1.5 space-y-0.5 list-disc list-inside">
                          <li><strong>Komma</strong> ≈ 150ms</li>
                          <li><strong>Punkt</strong> ≈ 400ms</li>
                          <li><strong>Em-Dash</strong> (—) ≈ mittlere Pause</li>
                          <li><strong>Ellipse</strong> (…) ≈ 700ms, dramatisch</li>
                          <li><strong>Doppelpunkt</strong> vor Aufzaehlung</li>
                        </ul>
                        <div className="mt-2 pt-2 border-t border-cream-200 dark:border-ink-700">
                          <span className="text-danger">Flach:</span> "Ectoin ist ein natuerliches Molekuel das die Haut vor Stress schuetzt"
                        </div>
                        <div className="mt-1 text-success">
                          Dynamisch: "Ectoin — ein natuerliches Molekuel. Es schuetzt die Haut, gerade unter Stress."
                        </div>
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-ink-50/60">
                    <span>{s.voice_over.length}/250 · ~{Math.ceil(s.voice_over.split(/\s+/).filter(Boolean).length / 3)}s speech</span>
                    {editable && (
                      <button
                        onClick={() => regenerateScene(idx)}
                        disabled={busyScene === idx}
                        className="text-brand-600 hover:underline flex items-center gap-1"
                      >
                        {busyScene === idx ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        Regenerate
                      </button>
                    )}
                  </div>
                  <SceneVoiceAdvanced
                    sceneSpeed={s.voice_settings?.speed ?? VOICE_SPEED_DEFAULT}
                    isCustom={s.voice_settings?.speed != null && s.voice_settings.speed !== VOICE_SPEED_DEFAULT}
                    disabled={!editable}
                    onChange={v => updateVoiceSpeed(idx, v)}
                    onCommit={() => commitVoiceSpeed(idx)}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// Searchable voice combobox (Backlog #3). Populated from GET /api/heygen/voices
// (proxied via marketing-api — atlas never calls HeyGen directly). Default
// option = "Avatar's default voice" (null) so an untouched picker submits the
// exact same payload as before. `q` search hits the full HeyGen catalog server-
// side; gender narrows further; preview plays HeyGen's sample clip when present.
function VoicePicker({
  value, onChange, disabled,
}: {
  value: string | null;
  onChange: (voiceId: string | null) => void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [voices, setVoices] = useState<Voice[]>([]);
  const [search, setSearch] = useState("");
  const [gender, setGender] = useState<"" | "female" | "male">("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Fetch on open + whenever search/gender changes (debounced). Server-side `q`
  // reaches the FULL catalog; the default (no-q) list is cached by the proxy.
  useEffect(() => {
    if (!open) return;
    const term = search.trim();
    const t = setTimeout(() => {
      setLoading(true);
      const parts = ["language=English", "limit=300"];
      if (gender) parts.push(`gender=${gender}`);
      if (term.length >= 2) parts.push(`q=${encodeURIComponent(term)}`);
      api.get<Voice[]>(`/api/heygen/voices?${parts.join("&")}`)
        .then((rows) => { setVoices(rows); setErr(null); })
        .catch((e) => setErr(e?.message || "could not load voices"))
        .finally(() => setLoading(false));
    }, term ? 300 : 0);
    return () => clearTimeout(t);
  }, [open, search, gender]);

  const selected = voices.find((v) => v.voice_id === value);
  const selectedLabel = value
    ? (selected?.name ?? `Voice ${value.slice(0, 8)}…`)
    : "Avatar's default voice";

  const term = search.trim().toLowerCase();
  const shown = term ? voices.filter((v) => v.name.toLowerCase().includes(term)) : voices;

  const preview = (url: string) => {
    if (audioRef.current) audioRef.current.pause();
    const a = new Audio(url);
    audioRef.current = a;
    a.play().catch(() => {});
  };

  const pick = (voiceId: string | null) => {
    onChange(voiceId);
    setOpen(false);
    setSearch("");
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={clsx(
          "flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs",
          "border-cream-300 dark:border-ink-600 bg-surface dark:bg-ink-900",
          "hover:border-brand-400 disabled:opacity-50 disabled:cursor-not-allowed",
          value ? "text-ink-50" : "text-ink-50/70",
        )}
        title="Choose the HeyGen voice for this video"
      >
        <span className="max-w-[180px] truncate">{selectedLabel}</span>
        <ChevronDown className="w-3 h-3 shrink-0" />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-72 rounded-md border border-cream-300 dark:border-ink-600 bg-cream-50 dark:bg-ink-800 shadow-lg p-2">
          <div className="flex items-center gap-1 mb-2">
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search voices by name…"
              className="flex-1 min-w-0 px-2 py-1 rounded-md border border-cream-300 dark:border-ink-600 bg-surface dark:bg-ink-900 text-xs"
            />
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value as "" | "female" | "male")}
              className="px-1 py-1 rounded-md border border-cream-300 dark:border-ink-600 bg-surface dark:bg-ink-900 text-xs"
              title="Filter by gender"
            >
              <option value="">any</option>
              <option value="female">female</option>
              <option value="male">male</option>
            </select>
          </div>
          {err && <div className="text-[10px] text-danger mb-1">{err}</div>}
          <div className="max-h-64 overflow-y-auto">
            <button
              type="button"
              onClick={() => pick(null)}
              className={clsx(
                "w-full text-left px-2 py-1.5 rounded-md text-xs flex items-center gap-2",
                "hover:bg-cream-200 dark:hover:bg-ink-700",
                !value && "bg-brand-100 dark:bg-brand-900/30",
              )}
            >
              <span className="flex-1">Avatar&apos;s default voice</span>
              {!value && <CheckCircle2 className="w-3.5 h-3.5 text-brand-600" />}
            </button>
            {loading && (
              <div className="flex items-center gap-1 px-2 py-2 text-[10px] text-ink-50/60">
                <Loader2 className="w-3 h-3 animate-spin" /> loading…
              </div>
            )}
            {!loading && shown.length === 0 && (
              <div className="px-2 py-2 text-[10px] text-ink-50/50">no voices match.</div>
            )}
            {shown.map((v) => (
              <div
                key={v.voice_id}
                className={clsx(
                  "w-full px-2 py-1.5 rounded-md text-xs flex items-center gap-2 group",
                  "hover:bg-cream-200 dark:hover:bg-ink-700",
                  value === v.voice_id && "bg-brand-100 dark:bg-brand-900/30",
                )}
              >
                <button
                  type="button"
                  onClick={() => pick(v.voice_id)}
                  className="flex-1 min-w-0 text-left"
                >
                  <span className="block truncate">{v.name}</span>
                  <span className="block text-[9px] text-ink-50/50 truncate">
                    {[v.language, v.gender].filter(Boolean).join(" · ")}
                  </span>
                </button>
                {v.preview_url && (
                  <button
                    type="button"
                    onClick={() => preview(v.preview_url!)}
                    className="p-1 rounded-md text-ink-50/50 hover:text-brand-600 hover:bg-cream-300 dark:hover:bg-ink-600"
                    title="Preview voice"
                  >
                    <Play className="w-3 h-3" />
                  </button>
                )}
                {value === v.voice_id && (
                  <CheckCircle2 className="w-3.5 h-3.5 text-brand-600 shrink-0" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SceneVoiceAdvanced({
  sceneSpeed, isCustom, disabled, onChange, onCommit,
}: {
  sceneSpeed: number;
  isCustom: boolean;
  disabled: boolean;
  onChange: (v: number) => void;
  onCommit: () => void;
}) {
  const [open, setOpen] = useState(isCustom);
  return (
    <div className="border-t border-cream-200 dark:border-ink-700 pt-1 mt-1">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 text-[10px] text-ink-50/60 hover:text-ink-50/90"
        type="button"
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        Voice (advanced)
        {isCustom && (
          <span className="ml-1 px-1 rounded-md bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300">
            speed {sceneSpeed.toFixed(2)}
          </span>
        )}
      </button>
      {open && (
        <div className="mt-1.5 pl-4 flex items-center gap-3 text-[10px] text-ink-50/70">
          <label className="flex-1 flex items-center gap-2">
            <span className="w-12 shrink-0">Speed</span>
            <input
              type="range" min={0.5} max={1.5} step={0.01}
              value={sceneSpeed}
              disabled={disabled}
              onChange={e => onChange(parseFloat(e.target.value))}
              onMouseUp={onCommit}
              onTouchEnd={onCommit}
              onBlur={onCommit}
              className="flex-1"
            />
            <span className="w-16 text-right font-mono">
              {sceneSpeed.toFixed(2)}
              {!isCustom && <span className="text-ink-50/40 ml-1">(default)</span>}
            </span>
          </label>
          {isCustom && (
            <button
              onClick={() => { onChange(VOICE_SPEED_DEFAULT); onCommit(); }}
              disabled={disabled}
              className="text-ink-50/50 hover:text-ink-50/80"
              title="Auf server-default 0.95 zuruecksetzen — sendet kein voice_settings an marketing-api mehr"
            >
              reset
            </button>
          )}
        </div>
      )}
    </div>
  );
}


function ComplianceViolations({ script }: {
  script: HeygenScript;
  onChange: (s: HeygenScript) => void;
}) {
  const vios = script.compliance_violations ?? [];
  return (
    <section className="bg-warning-soft border border-warning/30 rounded-lg p-4">
      <h2 className="font-medium text-warning flex items-center gap-2 mb-3">
        <AlertTriangle className="w-4 h-4" /> Compliance review needed ({vios.length})
      </h2>
      <ul className="space-y-3">
        {vios.map((v: any, i: number) => (
          <li key={i} className="bg-surface dark:bg-ink-900/50 border border-warning/30 rounded-md p-3 space-y-1.5">
            <div className="text-xs text-warning font-medium flex items-center gap-2">
              <span className="px-1.5 py-0.5 rounded-md bg-warning-soft">
                Scene {(v.scene_index ?? 0) + 1}
              </span>
              <span>{v.kind}</span>
              {v.similarity != null && (
                <span className="text-ink-50/60 font-mono">
                  cosine {(v.similarity as number).toFixed(3)} (threshold 0.60)
                </span>
              )}
            </div>
            <div className="text-xs text-ink-50/70">{v.reason}</div>
            {v.extracted && (
              <div className="text-sm font-serif italic text-ink border-l-2 border-warning/30 pl-2">
                "{v.extracted}"
              </div>
            )}
            {v.extra?.claim_label && (
              <div className="text-xs text-ink-50/70">
                <span className="font-medium">vs referenced claim:</span> <span className="italic">"{v.extra.claim_label}"</span>
              </div>
            )}
          </li>
        ))}
      </ul>
      <p className="text-xs text-warning mt-3">
        Optionen: markierte Szene(n) neu erzeugen, andere Claims wählen oder unten
        auf <strong>Trotzdem freigeben</strong> klicken, wenn das Voice-over eine
        legitime Umschreibung ist.
      </p>
    </section>
  );
}

// ----------------------------------------------------------------
// Polish-Tab (docs/heygen-polish-build-task.md §2.3)
// Lets the user pick caption-style, intro/outro cards, music + fades.
// Persisted in heygen_scripts.polish_settings; optionally saved as a
// per-avatar default in heygen_avatar_polish_defaults.
// ----------------------------------------------------------------

function PolishTab({ script, onChange }: {
  script: HeygenScript;
  onChange: (s: HeygenScript) => void;
}) {
  const [presets, setPresets] = useState<PolishPresets | null>(null);
  // Deep-merge der server-side polish_settings ueber POLISH_DEFAULTS.
  // Server-Werte gewinnen pro Feld. Fehlende feature-Bloecke werden mit
  // den aktuellen Defaults gefuellt. Dadurch sieht UI = DB nach erstem
  // Mount + Autosave persistiert die ergaenzten Defaults.
  const [draft, setDraft] = useState<PolishSettings>(() =>
    mergePolish(script.polish_settings)
  );
  const [busy, setBusy] = useState<"save-default" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [autosaveState, setAutosaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  // Tracks last draft we sent to backend; gate against redundant PATCHes.
  const lastSavedRef = useRef<string>(JSON.stringify(script.polish_settings ?? null));

  // PolishTab editable in allen Status — Polish veraendert nur das
  // ffmpeg-Postprocessing, nicht das HeyGen-Raw (siehe spec §7a).
  // Auch nach 'approved'/'exported' soll man captions umstellen koennen.
  const editable = true;

  // Re-polish-Fenster (§7a): 60d ab video_submitted_at. Danach 410 vom
  // marketing-api.
  const submittedAt = script.video_submitted_at
    ? new Date(script.video_submitted_at) : null;
  const repolishUntil = submittedAt
    ? new Date(submittedAt.getTime() + REPOLISH_WINDOW_DAYS * 24 * 3600 * 1000)
    : null;
  const repolishOpen = script.video_status === "completed"
    && repolishUntil != null
    && repolishUntil.getTime() > Date.now();

  // Load presets once
  useEffect(() => {
    let alive = true;
    api.get<PolishPresets>("/api/heygen/polish/presets")
      .then(p => { if (alive) setPresets(p); })
      .catch(e => { if (alive) setError(e?.message ?? "failed to load polish presets"); });
    return () => { alive = false; };
  }, []);

  // Load avatar-defaults when avatar changes AND script has no own polish_settings yet
  useEffect(() => {
    if (script.polish_settings) return;
    if (!script.avatar_id) return;
    let alive = true;
    api.get<{ avatar_id: string; settings: PolishSettings } | null>(
      `/api/heygen/avatars/${encodeURIComponent(script.avatar_id)}/polish-defaults`,
    ).then(row => {
      if (!alive) return;
      if (row && row.settings) {
        setDraft(mergePolish(row.settings as PolishSettings));
      }
    }).catch(() => { /* 404/empty = use spec defaults */ });
    return () => { alive = false; };
  }, [script.avatar_id, script.polish_settings]);

  const update = (patch: Partial<PolishSettings>) => {
    setDraft(d => ({ ...d, ...patch }));
    setNote(null);
  };

  // Auto-save: jeder Draft-Change wird debounced (500ms) zur Atlas-DB
  // gepushed. ROOT CAUSE FIX: vorher musste der User explizit "Save"
  // klicken bevor Generate-Video; tat er das nicht, blieb
  // script.polish_settings = NULL und marketing-api bekam polish: null →
  // alle Captions/Intro/Outro/Music wurden ignoriert.
  useEffect(() => {
    const serialized = JSON.stringify(draft);
    if (serialized === lastSavedRef.current) return;
    const timer = setTimeout(async () => {
      setAutosaveState("saving");
      try {
        const updated = await api.patch<HeygenScript>(
          `/api/heygen/scripts/${script.id}`,
          { polish_settings: draft },
        );
        lastSavedRef.current = serialized;
        setAutosaveState("saved");
        // Refresh script ohne den onChange-Trigger fuer Polish (sonst
        // Loop: parent rerender → neue draft-Initialisierung). Aktualisiere
        // den Parent damit re-polish-Fenster etc korrekt sind, behalte
        // aber den lokalen draft.
        onChange(updated);
        // Kurz "Saved" zeigen, dann zurueck auf idle
        setTimeout(() => setAutosaveState(s => s === "saved" ? "idle" : s), 1500);
      } catch (e: any) {
        setAutosaveState("error");
        setError(e?.message ?? "auto-save failed");
      }
    }, 500);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, script.id]);

  const saveAsAvatarDefault = async () => {
    setBusy("save-default"); setError(null); setNote(null);
    try {
      await api.put(
        `/api/heygen/avatars/${encodeURIComponent(script.avatar_id)}/polish-defaults`,
        { settings: draft },
      );
      setNote(`Saved as default for avatar ${script.avatar_id}.`);
    } catch (e: any) {
      setError(e?.message ?? "save-default failed");
    } finally {
      setBusy(null);
    }
  };

  const resetToDefaults = () => {
    setDraft(POLISH_DEFAULTS);
    setNote(null);
  };

  if (!presets) {
    return (
      <section className="bg-cream-50 dark:bg-ink-800 border border-cream-200 dark:border-ink-600 rounded-lg p-4">
        <div className="text-sm text-ink-50/60 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading polish presets…
        </div>
      </section>
    );
  }

  const captions = draft.captions ?? POLISH_DEFAULTS.captions!;
  const intro = draft.intro ?? POLISH_DEFAULTS.intro!;
  const outro = draft.outro ?? POLISH_DEFAULTS.outro!;
  const music = draft.background_music ?? POLISH_DEFAULTS.background_music!;
  const fade = draft.video_fade_s ?? 0.3;

  return (
    <section className="bg-cream-50 dark:bg-ink-800 border border-cream-200 dark:border-ink-600 rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-medium flex items-center gap-2">
          <Sparkles className="w-4 h-4" /> Polish (optional)
          {autosaveState === "saving" && (
            <span className="text-xs text-ink-50/50 flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> saving…
            </span>
          )}
          {autosaveState === "saved" && (
            <span className="text-xs text-success">✓ saved</span>
          )}
          {autosaveState === "error" && (
            <span className="text-xs text-danger">⚠ save failed</span>
          )}
        </h2>
        <div className="flex gap-2 text-sm">
          <button
            onClick={resetToDefaults}
            disabled={!editable || busy !== null}
            className="px-2 py-1 rounded-md border border-cream-300 dark:border-ink-600 text-ink-50/70 hover:bg-cream-100 dark:hover:bg-ink-700 disabled:opacity-50"
            title="Reset all polish picks to the bitop spec defaults"
          >
            Reset to defaults
          </button>
          <button
            onClick={saveAsAvatarDefault}
            disabled={!editable || busy !== null || !script.avatar_id}
            className="px-2 py-1 rounded-md border border-cream-300 dark:border-ink-600 text-ink-50/70 hover:bg-cream-100 dark:hover:bg-ink-700 disabled:opacity-50 flex items-center gap-1"
            title="Save these picks as the default whenever this avatar is used for a new script"
          >
            {busy === "save-default" && <Loader2 className="w-3 h-3 animate-spin" />}
            Save as avatar default
          </button>
        </div>
      </div>

      {repolishUntil && (
        <div className="text-xs text-ink-50/60">
          {repolishOpen
            ? `Re-Polish-Fenster offen bis ${repolishUntil.toLocaleDateString("de-DE")} (kostenloses Re-Rendering).`
            : `Re-Polish-Fenster abgelaufen (${repolishUntil.toLocaleDateString("de-DE")}). Polish-Aenderungen erfordern Re-Generate (kostet Guthaben).`}
        </div>
      )}

      {error && (
        <div className="text-sm text-danger bg-danger-soft border border-danger/30 rounded-md px-3 py-2">
          {error}
        </div>
      )}
      {note && !error && (
        <div className="text-sm text-success bg-success-soft border border-success/30 rounded-md px-3 py-2">
          {note}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PolishRow label={`Video fade (${fade.toFixed(2)}s)`}>
          <input
            type="range" min={0} max={2} step={0.1}
            disabled={!editable}
            value={fade}
            onChange={e => update({ video_fade_s: parseFloat(e.target.value) })}
            className="w-full max-w-md"
          />
        </PolishRow>
        <label className="flex items-center gap-2 text-sm self-end pb-1">
          <input
            type="checkbox"
            disabled={!editable}
            checked={draft.dramatic_pause_before_cta ?? true}
            onChange={e => update({ dramatic_pause_before_cta: e.target.checked })}
          />
          <span>Dramatische Pause vor CTA</span>
          <span className="text-xs text-ink-50/50">
            (haengt "..." an die Szene direkt vor dem CTA)
          </span>
        </label>
      </div>

      {/* Captions */}
      <PolishFeatureBlock
        label="Burn-in captions"
        enabled={captions.enabled}
        disabled={!editable}
        onToggle={en => update({ captions: { ...captions, enabled: en } })}
      >
        <PolishRow label="Style">
          <select
            disabled={!editable || !captions.enabled}
            value={captions.style}
            onChange={e => update({ captions: { ...captions, style: e.target.value } })}
            className="bg-cream-100 dark:bg-ink-900 border border-cream-300 dark:border-ink-600 rounded-md px-2 py-1 text-sm w-full"
          >
            {presets.caption_styles.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </PolishRow>
      </PolishFeatureBlock>

      {/* Intro */}
      <PolishFeatureBlock
        label="Intro card"
        enabled={intro.enabled}
        disabled={!editable}
        onToggle={en => update({ intro: { ...intro, enabled: en } })}
      >
        <PolishRow label="Preset">
          <select
            disabled={!editable || !intro.enabled}
            value={intro.preset}
            onChange={e => update({ intro: { ...intro, preset: e.target.value } })}
            className="bg-cream-100 dark:bg-ink-900 border border-cream-300 dark:border-ink-600 rounded-md px-2 py-1 text-sm w-full"
          >
            {presets.card_presets.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </PolishRow>
        <PolishRow label="Text">
          <input
            type="text"
            disabled={!editable || !intro.enabled}
            value={intro.text}
            onChange={e => update({ intro: { ...intro, text: e.target.value } })}
            className="bg-cream-100 dark:bg-ink-900 border border-cream-300 dark:border-ink-600 rounded-md px-2 py-1 text-sm w-full"
            maxLength={120}
          />
        </PolishRow>
        <PolishRow label={`Duration (${intro.duration_s.toFixed(2)}s)`}>
          <input
            type="range" min={0.4} max={3.0} step={0.1}
            disabled={!editable || !intro.enabled}
            value={intro.duration_s}
            onChange={e => update({ intro: { ...intro, duration_s: parseFloat(e.target.value) } })}
            className="w-full"
          />
        </PolishRow>
      </PolishFeatureBlock>

      {/* Outro */}
      <PolishFeatureBlock
        label="Outro card"
        enabled={outro.enabled}
        disabled={!editable}
        onToggle={en => update({ outro: { ...outro, enabled: en } })}
      >
        <PolishRow label="Preset">
          <select
            disabled={!editable || !outro.enabled}
            value={outro.preset}
            onChange={e => update({ outro: { ...outro, preset: e.target.value } })}
            className="bg-cream-100 dark:bg-ink-900 border border-cream-300 dark:border-ink-600 rounded-md px-2 py-1 text-sm w-full"
          >
            {presets.card_presets.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </PolishRow>
        <PolishRow label="CTA text">
          <input
            type="text"
            disabled={!editable || !outro.enabled}
            value={outro.text}
            onChange={e => update({ outro: { ...outro, text: e.target.value } })}
            className="bg-cream-100 dark:bg-ink-900 border border-cream-300 dark:border-ink-600 rounded-md px-2 py-1 text-sm w-full"
            maxLength={120}
          />
        </PolishRow>
        <PolishRow label={`Duration (${outro.duration_s.toFixed(2)}s)`}>
          <input
            type="range" min={0.4} max={3.0} step={0.1}
            disabled={!editable || !outro.enabled}
            value={outro.duration_s}
            onChange={e => update({ outro: { ...outro, duration_s: parseFloat(e.target.value) } })}
            className="w-full"
          />
        </PolishRow>
      </PolishFeatureBlock>

      {/* Music */}
      <PolishFeatureBlock
        label="Background music"
        enabled={music.enabled}
        disabled={!editable}
        onToggle={en => update({ background_music: { ...music, enabled: en } })}
      >
        <PolishRow label="Preset">
          <select
            disabled={!editable || !music.enabled}
            value={music.preset}
            onChange={e => update({ background_music: { ...music, preset: e.target.value } })}
            className="bg-cream-100 dark:bg-ink-900 border border-cream-300 dark:border-ink-600 rounded-md px-2 py-1 text-sm w-full"
          >
            {presets.music_presets.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </PolishRow>
        <PolishRow label={`Volume (${music.volume.toFixed(2)})`}>
          <input
            type="range" min={0} max={0.4} step={0.01}
            disabled={!editable || !music.enabled}
            value={music.volume}
            onChange={e => update({ background_music: { ...music, volume: parseFloat(e.target.value) } })}
            className="w-full"
          />
        </PolishRow>
        <PolishRow label={`Audio fade (${music.audio_fade_s.toFixed(2)}s)`}>
          <input
            type="range" min={0} max={2} step={0.1}
            disabled={!editable || !music.enabled}
            value={music.audio_fade_s}
            onChange={e => update({ background_music: { ...music, audio_fade_s: parseFloat(e.target.value) } })}
            className="w-full"
          />
        </PolishRow>
      </PolishFeatureBlock>

      <div className="text-xs text-ink-50/50 border-t border-cream-200 dark:border-ink-700 pt-2">
        Polish is applied post-HeyGen via marketing-api ffmpeg. Estimated extra render time ~30-60s, no additional HeyGen credit cost.
      </div>
    </section>
  );
}

// ----------------------------------------------------------------
// Reusable confirm modal — replaces window.confirm()
// ----------------------------------------------------------------

function ConfirmModal({
  open, title, body, confirmLabel, confirmTone = "brand",
  onConfirm, onCancel,
}: {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  confirmTone?: "brand" | "danger";
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;
  return (
    <Modal
      onClose={onCancel}
      title={title}
      size="sm"
      footer={
        <>
          <button
            onClick={onCancel}
            className="px-3 py-1.5 rounded-md border border-border text-sm hover:bg-surface-3 text-text"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={clsx(
              "px-3 py-1.5 rounded-md text-white text-sm",
              confirmTone === "brand"
                ? "bg-accent-500 hover:bg-accent-600"
                : "bg-danger hover:bg-danger/90",
            )}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      <p className="text-sm text-text-2 whitespace-pre-line">{body}</p>
    </Modal>
  );
}


function PolishRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-ink-50/70 text-xs">{label}</span>
      {children}
    </label>
  );
}

function PolishFeatureBlock({
  label, enabled, disabled, onToggle, children,
}: {
  label: string;
  enabled: boolean;
  disabled: boolean;
  onToggle: (v: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="border border-cream-200 dark:border-ink-700 rounded-md p-3 space-y-2">
      <label className="flex items-center gap-2 text-sm font-medium">
        <input
          type="checkbox"
          disabled={disabled}
          checked={enabled}
          onChange={e => onToggle(e.target.checked)}
        />
        {label}
      </label>
      {enabled && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pl-6">
          {children}
        </div>
      )}
    </div>
  );
}


/** HeyGen-Guthaben von `/api/heygen/credits`.
 *
 *  ⚠️ Das Feld heisst beim Dienst `remaining_credits`, der WERT sind aber
 *  US-DOLLAR. Am 2026-08-17 direkt gegen HeyGen gemessen: `billing_type`
 *  ist `wallet`, `currency` ist `usd`, und der Stand entsprach exakt den
 *  aufgeladenen 50 $. Die frühere Behauptung „1 Credit ≈ 0,30 $" gilt für
 *  dieses Konto nicht — deshalb steht hier überall ein Betrag, keine Stückzahl.
 *
 *  Drei Zustände, und sie MÜSSEN unterscheidbar bleiben: `undefined` = wird
 *  geladen, `null` = nicht abrufbar, Zahl = Stand. Ein Fehler darf hier nicht
 *  als „0" erscheinen — das sieht aus wie ein leeres Konto und schickt jemanden
 *  zum Nachkaufen, obwohl vielleicht nur der Dienst kurz weg war. Genau diese
 *  Verwechslung hat am 12.08. fünf Tage gekostet. */
function useGuthaben() {
  const [stand, setStand] = useState<number | null | undefined>(undefined);
  const laden = useCallback(() => {
    return api
      .get<{ remaining_credits: number | null }>("/api/heygen/credits")
      .then((r) => setStand(typeof r?.remaining_credits === "number" ? r.remaining_credits : null))
      .catch(() => setStand(null));
  }, []);
  useEffect(() => { laden(); }, [laden]);
  return { stand, laden };
}

/** Betrag menschenlesbar: „50,00 $". */
function usdText(n: number): string {
  return n.toLocaleString("de-DE", {
    style: "currency", currency: "USD", maximumFractionDigits: 2,
  });
}

function ApproveAndVideoSection({ script, onChange }: {
  script: HeygenScript;
  onChange: (s: HeygenScript) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [busyAction, setBusyAction] = useState<"approve" | "video" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitNote, setSubmitNote] = useState<string | null>(null);
  const { stand: guthaben, laden: guthabenNeuLaden } = useGuthaben();

  const approve = async (override: boolean = false) => {
    setBusy(true); setBusyAction("approve"); setError(null); setSubmitNote(null);
    try {
      const url = override
        ? `/api/heygen/scripts/${script.id}/approve?override_compliance=true`
        : `/api/heygen/scripts/${script.id}/approve`;
      const updated = await api.post<HeygenScript>(url);
      onChange(updated);
    } catch (e: any) {
      const msg = e?.message || "approve failed";
      if (msg.toLowerCase().includes("compliance check failed")) {
        // Refresh to load the freshly-persisted violations (now in the row
        // because backend commits before raising).
        const fresh = await api.get<HeygenScript>(`/api/heygen/scripts/${script.id}`).catch(() => null);
        if (fresh) onChange(fresh);
        setError(msg);
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false); setBusyAction(null);
    }
  };

  const [confirmKind, setConfirmKind] = useState<"first" | "regenerate" | null>(null);

  // Re-polish-Fenster (§7a): 60d nach video_submitted_at
  const submittedAt = script.video_submitted_at
    ? new Date(script.video_submitted_at) : null;
  const repolishUntil = submittedAt
    ? new Date(submittedAt.getTime() + REPOLISH_WINDOW_DAYS * 24 * 3600 * 1000)
    : null;
  const repolishOpen = script.video_status === "completed"
    && repolishUntil != null
    && repolishUntil.getTime() > Date.now();

  const doGenerateVideo = async () => {
    setConfirmKind(null);
    setBusy(true); setBusyAction("video"); setError(null); setSubmitNote(null);
    try {
      const resp = await api.post<{
        job_id: string; idempotent: boolean; video_status: string;
        video_error: string | null; wallet_usd_before: number | null;
      }>(`/api/heygen/scripts/${script.id}/generate-video`);
      // Der wichtigste Fall zuerst: die marketing-api gibt einen FRÜHEREN,
      // gescheiterten Auftrag zurück, statt einen neuen zu starten (sie
      // dedupliziert über den Skript-Inhalt, ohne Status- und Zeitfilter).
      // Ohne diesen Hinweis sieht „es ist nichts passiert" exakt aus wie
      // „abgeschickt und wieder fehlgeschlagen" — und man klickt endlos.
      if (resp.video_status === "failed") {
        setSubmitNote(null);
        setError(
          `Es wurde KEIN neuer Auftrag gestartet: HeyGen hat diesen Text schon ` +
          `einmal abgelehnt (Auftrag ${resp.job_id.slice(0, 8)}…). Grund: ` +
          `${resp.video_error || "unbekannt"} — ändere den Text einer Szene ` +
          `(oder eine Polish-Einstellung), dann läuft ein echter neuer Versuch.`
        );
      } else {
        setSubmitNote(
          (resp.idempotent
            ? `Läuft bereits (Auftrag ${resp.job_id.slice(0, 8)}…) — warte auf die Fertigmeldung.`
            : `An HeyGen geschickt (Auftrag ${resp.job_id.slice(0, 8)}…) — dauert typisch 3-5 Min.`)
          + (resp.wallet_usd_before !== null
            ? ` Guthaben vor dem Start: ${usdText(resp.wallet_usd_before)}.`
            : "")
        );
      }
      // ⚠️ NICHT neu laden, sondern die Antwort uebernehmen.
      //
      // `get_session` (app/db.py:44-50) committet im Teardown der Dependency —
      // also NACHDEM die Antwort rausgegangen ist. Ein GET unmittelbar danach
      // liest deshalb den Stand VOR der Absendung. Gemessen am 17.08.2026:
      // Auftrag lief nachweislich (`video_status=generating` in der DB),
      // waehrend die Oberflaeche „Fehlgeschlagen" von vorher zeigte — und dort
      // kleben blieb, weil die Auto-Aktualisierung unten nur bei
      // `queued`/`generating` anspringt.
      //
      // Die POST-Antwort ist fuer die Video-Felder ohnehin die genauere Quelle;
      // alles Uebrige holt die Auto-Aktualisierung nach.
      onChange({
        ...script,
        video_status: resp.video_status,
        marketing_api_job_id: resp.job_id,
        video_error: resp.video_error,
        wallet_usd_before:
          resp.wallet_usd_before !== null ? String(resp.wallet_usd_before) : null,
      });
      guthabenNeuLaden();
    } catch (e: any) {
      const msg = e?.message || "video generation failed";
      if (msg.toLowerCase().includes("failed to fetch")) {
        setError(
          "Network/proxy timeout. The submission may still have succeeded — " +
          "wait 10s and refresh the page. Do NOT click Generate Video again."
        );
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false); setBusyAction(null);
    }
  };

  const doRepolishVideo = async () => {
    setBusy(true); setBusyAction("video"); setError(null); setSubmitNote(null);
    try {
      const updated = await api.post<HeygenScript>(
        `/api/heygen/scripts/${script.id}/repolish-video`
      );
      onChange(updated);
      setSubmitNote("Re-polish complete — video updated (no credit consumed).");
    } catch (e: any) {
      const msg = e?.message || "re-polish failed";
      // marketing-api 410 covers: (a) raw-MP4 cleanup after 60d, (b) video
      // submitted before raw-storage rollout (2026-06-22). Atlas forwards
      // the upstream `detail` field, das schon den Grund erklaert.
      if (msg.includes("410") || msg.toLowerCase().includes("raw mp4 not available")) {
        setError(
          "Re-polish fuer dieses Video nicht verfuegbar: das Raw-MP4 existiert " +
          "nicht (entweder >60 Tage alt, ODER vor dem Raw-Storage-Feature " +
          "submittet, das am 2026-06-22 live ging). " +
          "Nutze 'Re-generate video' (kostet Guthaben) — fuer alle neuen Videos wird " +
          "Re-polish dann funktionieren.",
        );
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false); setBusyAction(null);
    }
  };

  return (
    <section className="bg-cream-50 dark:bg-ink-800 border border-cream-200 dark:border-ink-600 rounded-lg p-4">
      <h2 className="font-medium mb-3 flex items-center gap-2">
        <Video className="w-4 h-4" /> Approve + Generate Video
      </h2>

      {busyAction === "video" && (
        <div className="text-sm text-accent-700 bg-accent-50 border border-accent-200 rounded-md px-3 py-2 mb-2 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Submitting to marketing-api… (HeyGen submission takes ~3-8s, do NOT click again)</span>
        </div>
      )}
      {submitNote && !busy && (
        <div className="text-sm text-success bg-success-soft border border-success/30 rounded-md px-3 py-2 mb-2">
          ✓ {submitNote}
        </div>
      )}
      {error && <div className="text-sm text-danger bg-danger-soft border border-danger/30 rounded-md px-3 py-2 mb-2">{error}</div>}

      <div className="flex flex-wrap gap-2 mb-4">
        {(script.status === "generated" || script.status === "compliance_blocked") && (
          <button
            onClick={() => approve(false)}
            disabled={busy}
            className="px-3 py-1.5 rounded-md bg-success-soft text-white text-sm hover:bg-success-soft disabled:opacity-50 flex items-center gap-1"
          >
            {busyAction === "approve" ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
            Freigeben {script.status === "compliance_blocked" && "(Compliance neu prüfen)"}
          </button>
        )}
        {(script.status === "generated" || script.status === "compliance_blocked")
          && script.compliance_violations && script.compliance_violations.length > 0 && (
          <button
            onClick={() => approve(true)}
            disabled={busy}
            className="px-3 py-1.5 rounded-md border border-warning text-warning text-sm hover:bg-warning-soft disabled:opacity-50 flex items-center gap-1"
            title="Skip compliance gate — only do this if you judge the flagged voice-overs as legitimate paraphrases."
          >
            <AlertTriangle className="w-3 h-3" />
            Trotzdem freigeben (Compliance übergehen)
          </button>
        )}
        {script.status === "approved" && !script.video_status && (
          <Button
            onClick={() => setConfirmKind("first")}
            disabled={busy}
            loading={busyAction === "video"}
            icon={<Play className="w-3 h-3" />}
            title="Sendet das Skript an HeyGen. Wird vom Prepaid-Guthaben abgebucht (Abrechnung nach Videolaenge)."
          >
            {busyAction === "video" ? "Wird gesendet …" : "Video erzeugen"}
          </Button>
        )}
        {script.status === "approved" && (script.video_status === "failed" || script.video_status === "completed") && (
          <>
            <Button
              onClick={() => setConfirmKind("regenerate")}
              disabled={busy}
              loading={busyAction === "video"}
              icon={<RefreshCcw className="w-3 h-3" />}
              title="Komplett-neuer HeyGen-Render. Wird vom Guthaben abgebucht, auch wenn nur Polish-Einstellungen geändert wurden — dafür nutze Re-polish stattdessen."
            >
              Video neu erzeugen
            </Button>
            {repolishOpen && (
              <button
                onClick={doRepolishVideo}
                disabled={busy}
                className="px-3 py-1.5 rounded-md border border-brand-500 text-brand-600 dark:text-brand-300 text-sm hover:bg-brand-50 dark:hover:bg-brand-900/20 disabled:opacity-50 flex items-center gap-1"
                title={`Rendert nur den ffmpeg-Polish neu (Captions/Cards/Music/Fades) — kein HeyGen-Credit. Verfuegbar bis ${repolishUntil!.toLocaleDateString("de-DE")}.`}
              >
                {busyAction === "video" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                Re-polish video (free)
              </button>
            )}
          </>
        )}
        {/* Guthaben direkt neben den Knöpfen, die es kosten — nicht in einem
            Reiter, den man erst suchen muss. Vor dem 17.08. war es nirgends
            sichtbar, und ein leeres Konto sah aus wie ein Defekt. */}
        {script.status === "approved" && (
          <span
            className="text-xs text-text-2 self-center"
            title={
              guthaben === null
                ? "Das Guthaben konnte nicht abgerufen werden (marketing-api/HeyGen). Das ist KEINE Aussage über den tatsächlichen Stand."
                : "Prepaid-Guthaben des HeyGen-Kontos. HeyGen rechnet pro Video nach Länge ab, nicht in Stück."
            }
          >
            {guthaben === undefined
              ? "Guthaben wird geladen …"
              : guthaben === null
                ? "⚠ Guthaben nicht abrufbar"
                : `${usdText(guthaben)} Guthaben`}
          </span>
        )}
      </div>

      <ConfirmModal
        open={confirmKind !== null}
        title={confirmKind === "first" ? "An HeyGen schicken?" : "Video neu erzeugen?"}
        body={(confirmKind === "first"
          ? "Dieses Skript geht über die marketing-api an HeyGen und wird vom Prepaid-Guthaben abgebucht. Bei unverändertem Text wird KEIN neuer Auftrag gestartet, sondern der frühere zurückgegeben — auch ein gescheiterter."
          : "Komplett-neuer HeyGen-Render, wird vom Guthaben abgebucht — auch wenn nur Polish-Einstellungen geändert wurden. Für reine Polish-Änderungen 'Re-polish' nutzen (kostenlos).")
          + (guthaben === undefined
            ? ""
            : guthaben === null
              ? " ⚠ Das Guthaben ist gerade nicht abrufbar — das heisst NICHT, dass keines da ist."
              : ` Verfügbar: ${usdText(guthaben)}.`)}
        confirmLabel={confirmKind === "first" ? "An HeyGen senden" : "Neu erzeugen"}
        confirmTone="brand"
        onConfirm={doGenerateVideo}
        onCancel={() => setConfirmKind(null)}
      />

      {script.video_status === "queued" || script.video_status === "generating" ? (
        <div className="flex items-center gap-2 text-sm text-ink-50/70">
          <Loader2 className="w-4 h-4 animate-spin" />
          Video wird generiert (≈5 Min)…
          {script.marketing_api_job_id && (
            <span className="text-xs text-ink-50/50 font-mono">job: {script.marketing_api_job_id}</span>
          )}
        </div>
      ) : null}

      {script.video_status === "failed" && (
        <div className="text-sm text-danger">
          {script.video_error
            ? `Fehlgeschlagen: ${script.video_error}`
            : 'Fehlgeschlagen — der Grund ist nicht gespeichert. Ein Klick auf „Video neu erzeugen" holt ihn nach.'}
        </div>
      )}

      {/* Was der fertige Render gekostet hat. Differenz zweier Messungen des
          Guthabens (Mig 0100) — deshalb steht bei Alt-Videos und bei nicht
          messbarem Stand nichts, statt einer erfundenen Null. `> 0` schliesst
          den Fall aus, dass HeyGen auf den zweiten Topf (`plan_credit`)
          gebucht hat: dann bewegt sich die Börse nicht, und „0,00 $" würde als
          „war umsonst" gelesen. */}
      {script.video_status === "completed" && Number(script.wallet_usd_used) > 0 && (
        <div className="text-xs text-text-2">
          Verbraucht: {usdText(Number(script.wallet_usd_used))}
          {guthaben != null && ` · ${usdText(guthaben)} übrig`}
        </div>
      )}

      {script.video_status === "completed" && script.video_storage_url && (
        <div className="space-y-3">
          <video
            key={script.video_storage_url}
            controls
            playsInline
            preload="metadata"
            poster={script.video_thumbnail_url ?? undefined}
            className="rounded-md border border-cream-200 dark:border-ink-600 bg-black block mx-auto w-auto max-w-full"
            style={{ maxHeight: "70vh" }}
            src={script.video_storage_url}
          />
          <div className="flex items-center gap-3 text-xs text-ink-50/70">
            {script.video_duration_s != null && <span>{script.video_duration_s}s</span>}
            {/* Locale FEST auf de-DE — ohne Argument formatiert der Browser in
                seiner eigenen Sprache (englisches Chrome: US-Datum). */}
            {script.video_completed_at && (
              <span>Generated {new Date(script.video_completed_at).toLocaleString("de-DE")}</span>
            )}
            {script.video_cost_usd && <span>${script.video_cost_usd}</span>}
            <span className="ml-auto flex gap-2">
              <a
                href={script.video_storage_url}
                download
                className="text-brand-600 hover:underline flex items-center gap-1"
              >
                <Download className="w-3 h-3" /> Download MP4
              </a>
            </span>
          </div>
        </div>
      )}
    </section>
  );
}

function ResearchSourcesPanel({ script }: { script: HeygenScript }) {
  const [open, setOpen] = useState(false);
  const cits = script.research_citations ?? [];
  // Locale FEST auf de-DE (siehe oben) — sonst browserabhängiges Format.
  const fetchedAt = script.research_fetched_at
    ? new Date(script.research_fetched_at).toLocaleString("de-DE")
    : null;
  return (
    <section className="bg-cream-100 dark:bg-ink-900/50 border border-cream-200 dark:border-ink-700 rounded-lg">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-2 flex items-center justify-between text-left text-xs text-ink-50/70 hover:bg-cream-200/50 dark:hover:bg-ink-800/50 rounded-lg"
      >
        <span className="flex items-center gap-2">
          {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          Research sources ({script.research_model ?? "perplexity"}
          {fetchedAt && `, fetched ${fetchedAt}`}
          {cits.length > 0 && ` · ${cits.length} citations`})
        </span>
        <span className="text-ink-50/40">framing only — not cited as facts in scenes</span>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-2 border-t border-cream-200 dark:border-ink-700 space-y-3">
          <div className="text-sm whitespace-pre-wrap font-serif text-ink leading-relaxed">
            {script.research_background}
          </div>
          {cits.length > 0 && (
            <div className="border-t border-cream-200 dark:border-ink-700 pt-2">
              <div className="text-xs uppercase tracking-wider text-ink-50/50 mb-1">Citations</div>
              <ol className="text-xs space-y-0.5">
                {cits.map((c) => (
                  <li key={c.index} className="flex items-start gap-2">
                    <span className="text-ink-50/40 font-mono shrink-0">[{c.index}]</span>
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-600 hover:underline truncate"
                    >
                      {c.title || c.url}
                    </a>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
